"use client";

import { LocaleLink } from "@/components/i18n/LocaleLink";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { signIn, signInWithRedirect } from "aws-amplify/auth";
import { logMagicLinkEvent } from "@/lib/auth/magic-link-audit-client";
import { isDisabledAccountError } from "@/lib/auth/sign-in-errors";
import { syncCookieFromSession } from "@/components/auth/AccessTokenCookieMirror";
import type { SignInPageStrings } from "@/lib/i18n/auth-strings";
import { MAGNAFLOR_PHONE_DISPLAY } from "@/lib/contact-info";

// PASTE #244: Google federation stays ENABLED for launch - the app-layer
// approval gate (middleware -> /pending-approval, all login methods)
// makes it safe. This flag only hides the button DURING the gate
// rollout: it ships off so no new Google sign-in can slip in before the
// gate is live, then is flipped on (NEXT_PUBLIC_ENABLE_GOOGLE_SIGNIN=true
// in prod) once the gate is deployed and verified. Not a permanent kill
// switch - the end state is Google enabled.
const GOOGLE_SIGNIN_ENABLED =
  process.env.NEXT_PUBLIC_ENABLE_GOOGLE_SIGNIN === "true";

// Step 10.5 commit 6 / #326 Phase B: Tesla split-screen sign-in form. The
// three Cognito flows are unchanged (password sign-in via signIn; Google via
// signInWithRedirect; magic-link request via the #325 force-mint endpoint).
// #326 collapsed the former magic|password tabs into ONE form: password is the
// default/primary mode, and a PROMINENT "use a magic link instead" link flips
// to magic mode in place (its data-testid="magic-link-entry" is preserved so
// the existing e2e regression still targets the same affordance).

// #326 Phase B: one form. Password is the default/primary mode; the magic-link
// request is reached via a prominent link that flips to "magic" mode in place.
type Mode = "password" | "magic";
type MagicStatus = "idle" | "sending" | "sent" | "error";
type PasswordStatus = "idle" | "submitting";

export function TeslaSignInForm({ strings }: { strings: SignInPageStrings }) {
  const router = useRouter();
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [magicStatus, setMagicStatus] = useState<MagicStatus>("idle");
  const [pwStatus, setPwStatus] = useState<PasswordStatus>("idle");
  const [pwError, setPwError] = useState<string | null>(null);

  function toMagic() {
    setPwError(null);
    setMagicStatus("idle");
    setMode("magic");
  }

  function toPassword() {
    setMagicStatus("idle");
    setMode("password");
  }

  async function handleMagic(e: React.FormEvent) {
    e.preventDefault();
    setMagicStatus("sending");
    const trimmedEmail = email.trim().toLowerCase();
    setSubmittedEmail(trimmedEmail);
    // #325 (Phase A): request the link via the server force-mint endpoint,
    // decoupled from the Cognito CreateAuthChallenge trigger, so an explicit
    // request is never suppressed by shouldSkipRemint (#324 bug). The endpoint
    // enforces the rate limit and silently refuses unknown / disabled /
    // not-approved emails (anti-enumeration), so every accepted request shows
    // the same sent state. We no longer call Amplify signIn to mint.
    try {
      await fetch("/api/auth/magic-link/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail }),
      });
      logMagicLinkEvent("requested", trimmedEmail);
      setMagicStatus("sent");
    } catch {
      logMagicLinkEvent("failed", trimmedEmail, "request-endpoint-unreachable");
      setMagicStatus("error");
    }
  }

  async function handlePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwStatus("submitting");
    setPwError(null);
    // #438: the pool uses UsernameAttributes:["email"], and Cognito SRP is
    // case- AND whitespace-SENSITIVE on that username. Signup stores the email
    // lowercased+trimmed (route: input.email.trim().toLowerCase()), so a client
    // who set a correct password but re-types their email with mobile auto-
    // capitalization ("Designs@...") or an autofilled trailing space gets
    // "Incorrect username or password" - which reads as "my password is broken"
    // and pushes them to the magic link (which already normalizes, so it works).
    // Normalize here to MATCH what was stored + what handleMagic already does.
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const result = await signIn({ username: normalizedEmail, password });
      if (result.isSignedIn) {
        // Same race fix as the Step 8c sign-in page: write the
        // middleware cookie before navigating so /dashboard doesn't
        // bounce back to /sign-in.
        await syncCookieFromSession();
        router.push("/dashboard");
      } else if (result.nextStep.signInStep === "CONFIRM_SIGN_UP") {
        router.push(
          `/sign-up/confirm?email=${encodeURIComponent(normalizedEmail)}`,
        );
      } else {
        setPwError(`${strings.signInFailed}: ${result.nextStep.signInStep}`);
      }
    } catch (err) {
      setPwError(mapSignInError(err, strings));
    } finally {
      setPwStatus("idle");
    }
  }

  async function handleGoogle() {
    setPwError(null);
    try {
      await signInWithRedirect({ provider: "Google" });
    } catch (err) {
      setPwError(
        err instanceof Error ? err.message : strings.signInFailed,
      );
    }
  }

  return (
    <div className="w-full max-w-[420px]">
      <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-teal">
        {strings.eyebrow}
      </div>
      <h2 className="mb-3 text-[clamp(32px,4vw,44px)] font-bold leading-[1.1] tracking-[-0.025em] text-brand-text">
        {strings.welcomeBack}
      </h2>
      <p className="mb-10 text-base text-brand-text-muted">
        {strings.formSub}
      </p>

      {mode === "password" ? (
        <>
          <form
            onSubmit={handlePassword}
            data-testid="password-form"
            className="flex flex-col gap-5"
          >
            <FormField
              label={strings.emailLabel}
              type="email"
              value={email}
              onChange={setEmail}
              autoFocus
              placeholder={strings.emailPlaceholder}
              autoComplete="email"
            />
            <FormField
              label={strings.passwordLabel}
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
            />
            {pwError && (
              <p role="alert" className="text-sm text-red-600">
                {pwError}
              </p>
            )}
            <SubmitButton
              data-testid="password-submit"
              disabled={pwStatus === "submitting"}
              label={
                pwStatus === "submitting"
                  ? strings.passwordSubmitting
                  : strings.passwordSubmit
              }
            />
          </form>

          {/* #326 Phase B: prominent magic-link entry. It is the PRIMARY path
              for the launch cohort who still hold an unknown random password,
              so it is a full-width affordance, not fine print. Keeps the
              data-testid="magic-link-entry" the e2e regression targets. */}
          <button
            type="button"
            data-testid="magic-link-entry"
            onClick={toMagic}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border border-brand-teal/40 bg-brand-teal-soft px-6 py-3.5 text-sm font-semibold text-brand-teal transition-colors hover:bg-brand-teal/10"
          >
            <SparkleIcon />
            {strings.forgotPassword}
          </button>
        </>
      ) : magicStatus === "sent" ? (
        <div
          data-testid="magic-request-sent"
          className="rounded-2xl border border-brand-teal/40 bg-brand-teal-soft p-6"
        >
          <p className="text-base font-semibold text-brand-text">
            {strings.magicSentTitle}
          </p>
          <p
            data-testid="magic-request-sent-detail"
            className="mt-2 text-sm font-medium leading-relaxed text-brand-text"
          >
            {strings.magicSentDetail.replace("{email}", submittedEmail)}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-brand-text-muted">
            {strings.magicSentBody}
          </p>
          <button
            type="button"
            onClick={toPassword}
            className="mt-4 text-sm font-semibold text-brand-teal hover:text-brand-teal-dark"
          >
            {strings.backToPasswordSignIn}
          </button>
        </div>
      ) : (
        <form
          onSubmit={handleMagic}
          data-testid="magic-request-form"
          className="flex flex-col gap-5"
        >
          <div className="flex items-start gap-3 rounded-xl border border-brand-teal/30 bg-brand-teal-soft p-4">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-teal text-white">
              <SparkleIcon />
            </div>
            <div className="text-[13px] leading-relaxed text-brand-text-muted">
              <strong className="font-bold text-brand-text">
                {strings.magicBadge.heading}
              </strong>{" "}
              {strings.magicBadge.body}
            </div>
          </div>

          <FormField
            label={strings.emailLabel}
            type="email"
            value={email}
            onChange={setEmail}
            autoFocus
            placeholder={strings.emailPlaceholder}
            autoComplete="email"
          />
          {magicStatus === "error" && (
            <p role="alert" className="text-sm text-red-600">
              {strings.signInFailed}
            </p>
          )}
          <SubmitButton
            data-testid="magic-request-submit"
            disabled={magicStatus === "sending"}
            label={
              magicStatus === "sending"
                ? strings.magicSubmitting
                : strings.magicSubmit
            }
          />
          <button
            type="button"
            onClick={toPassword}
            className="self-start text-sm font-semibold text-brand-teal hover:text-brand-teal-dark"
          >
            {strings.backToPasswordSignIn}
          </button>
        </form>
      )}

      {GOOGLE_SIGNIN_ENABLED ? (
        <>
          <div className="my-8 flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.15em] text-brand-text-muted">
            <span className="h-px flex-1 bg-brand-border" />
            <span>{strings.divider}</span>
            <span className="h-px flex-1 bg-brand-border" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            data-testid="google-signin"
            className="inline-flex h-12 w-full items-center justify-center gap-3 rounded-full border border-brand-border bg-white px-6 text-sm font-semibold text-brand-text transition-colors hover:bg-brand-surface"
          >
            <GoogleG />
            {strings.googleButton}
          </button>
        </>
      ) : null}

      <p className="mt-6 text-center text-sm text-brand-text-muted">
        {strings.newToMagnaflor}{" "}
        <LocaleLink
          href="/sign-up"
          className="font-semibold text-brand-teal hover:text-brand-teal-dark"
        >
          {strings.applyLink} →
        </LocaleLink>
      </p>
      <p className="mt-3 text-center text-xs text-brand-text-muted">
        {strings.helpLineLabel}{" "}
        <a
          href="mailto:sales@magnaflor.com"
          className="font-medium text-brand-text hover:text-brand-teal"
        >
          sales@magnaflor.com
        </a>{" "}
        · {MAGNAFLOR_PHONE_DISPLAY}
      </p>
    </div>
  );
}

// ---- helpers --------------------------------------------------------

function FormField({
  label,
  type,
  value,
  onChange,
  autoFocus,
  placeholder,
  autoComplete,
}: {
  label: string;
  type: "email" | "password";
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs font-bold uppercase tracking-[0.06em] text-brand-text-muted">
        {label}
      </span>
      <input
        type={type}
        required
        autoFocus={autoFocus}
        suppressHydrationWarning
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border border-brand-border bg-white px-4 py-4 text-base text-brand-text transition-all focus-visible:border-brand-teal focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-teal/20"
      />
    </label>
  );
}

function SubmitButton({
  label,
  disabled,
  "data-testid": dataTestId,
}: {
  label: string;
  disabled?: boolean;
  "data-testid"?: string;
}) {
  return (
    <button
      type="submit"
      disabled={disabled}
      data-testid={dataTestId}
      className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-brand-teal text-[13px] font-semibold uppercase tracking-[0.05em] text-white transition-colors hover:bg-brand-teal-dark disabled:opacity-60"
    >
      {label}
      <span aria-hidden>→</span>
    </button>
  );
}

function SparkleIcon() {
  return (
    <svg
      aria-hidden
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2zm6 12l1 2.8L22 18l-3 1.2L18 22l-1-2.8L14 18l3-1.2L18 14z" />
    </svg>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.61z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
        fill="#EA4335"
      />
    </svg>
  );
}

function mapSignInError(err: unknown, strings: SignInPageStrings): string {
  if (isDisabledAccountError(err)) {
    return strings.accountInactive;
  }
  if (!(err instanceof Error)) return strings.signInFailed;
  const msg = err.message ?? "";
  if (/Incorrect username or password/i.test(msg)) {
    return strings.passwordError;
  }
  return msg || strings.signInFailed;
}
